import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import { ArrowBack as ArrowBackIcon, Download as DownloadIcon, Payments as PaymentsIcon } from '@mui/icons-material';
import { formatCents } from '../financials/api';
import { downloadExpenseLedgerCsv, getExpenseDocument, listExpenseLedger } from './api';
import ExpenseDraftReview from './ExpenseDraftReview';

const ALL = '';
const ALL_DATES = 'all';
const CUSTOM = 'custom';

/** Inclusive YYYY-MM-DD bounds for the chosen period; tax years are calendar years. */
function periodRange(period, from, to) {
  if (period === ALL_DATES) return {};
  if (period === CUSTOM) return { from: from || undefined, to: to || undefined };
  return { from: `${period}-01-01`, to: `${period}-12-31` };
}

/** YYYY-MM-DD as a local calendar date (no time zone shift). */
function formatDay(day) {
  const [y, m, d] = String(day || '').split('-').map(Number);
  if (!y || !m || !d) return '';
  return new Date(y, m - 1, d).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

function placeLabel(row) {
  const property = row.propertyName || 'Property';
  return row.unitName ? `${property}, ${row.unitName}` : property;
}

/** One total per currency, in the order the currencies first appear. */
function totalsByCurrency(rows) {
  const totals = new Map();
  for (const row of rows) {
    const t = totals.get(row.currency) || { currency: row.currency, amountCents: 0, taxCents: 0 };
    t.amountCents += row.amountCents || 0;
    t.taxCents += row.taxAmountCents || 0;
    totals.set(row.currency, t);
  }
  return [...totals.values()];
}

/** One total per CRA T776 line (and currency), in line order; rows without a line come last. */
function totalsByCraLine(rows) {
  const totals = new Map();
  for (const row of rows) {
    const key = `${row.craLine || row.categoryLabel || ''}|${row.currency}`;
    const t = totals.get(key) || {
      key,
      craLine: row.craLine || null,
      label: row.categoryLabel || 'Uncategorized',
      currency: row.currency,
      count: 0,
      amountCents: 0,
      taxCents: 0
    };
    t.count += 1;
    t.amountCents += row.amountCents || 0;
    t.taxCents += row.taxAmountCents || 0;
    totals.set(key, t);
  }
  return [...totals.values()].sort(
    (a, b) =>
      (a.craLine ? 0 : 1) - (b.craLine ? 0 : 1) ||
      String(a.craLine || a.label).localeCompare(String(b.craLine || b.label)) ||
      String(a.currency).localeCompare(String(b.currency))
  );
}

function errorMessage(err, fallback) {
  if (err?.response?.status === 404) return 'Expenses are not turned on for this account yet.';
  return err?.response?.data?.error || fallback;
}

/**
 * ZRF-EXP-CAP — expenses added to the books from receipts (expense ledger).
 * Shown inside Financial Center when isExpenseCaptureEnabled().
 * @param {{ onBack?: () => void, onOpenReceipts?: () => void }} props
 */
function ExpenseLedger({ onBack, onOpenReceipts }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [propertyId, setPropertyId] = useState(ALL);
  const [period, setPeriod] = useState(ALL_DATES);
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState(null);
  const [review, setReview] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const data = await listExpenseLedger();
      setRows(Array.isArray(data) ? data : []);
    } catch (err) {
      setLoadError(errorMessage(err, 'Could not load your expenses.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const properties = useMemo(() => {
    const seen = new Map();
    for (const row of rows) {
      if (row.propertyId && !seen.has(row.propertyId)) seen.set(row.propertyId, row.propertyName || 'Property');
    }
    return [...seen.entries()].map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [rows]);

  const years = useMemo(
    () =>
      [...new Set(rows.map((r) => String(r.transactionDate || '').slice(0, 4)).filter((y) => /^\d{4}$/.test(y)))]
        .sort()
        .reverse(),
    [rows]
  );

  const range = periodRange(period, customFrom, customTo);
  const rangeError = range.from && range.to && range.from > range.to ? 'The end date is before the start date.' : null;
  const shown = rangeError
    ? []
    : rows.filter(
        (r) =>
          (propertyId === ALL || r.propertyId === propertyId) &&
          (!range.from || String(r.transactionDate) >= range.from) &&
          (!range.to || String(r.transactionDate) <= range.to)
      );
  const totals = totalsByCurrency(shown);
  const lineTotals = totalsByCraLine(shown);

  const download = async () => {
    setDownloading(true);
    setDownloadError(null);
    try {
      await downloadExpenseLedgerCsv({ propertyId: propertyId || undefined, ...range });
    } catch (err) {
      setDownloadError('Could not download the file. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  const openReceipt = async (row) => {
    setReview({ id: row.documentId, phase: 'loading' });
    try {
      const doc = await getExpenseDocument(row.documentId);
      setReview((prev) => (prev?.id === row.documentId ? { id: row.documentId, phase: 'ready', doc } : prev));
    } catch (err) {
      setReview((prev) =>
        prev?.id === row.documentId
          ? { id: row.documentId, phase: 'error', error: errorMessage(err, 'Could not open this receipt.') }
          : prev
      );
    }
  };

  return (
    <Box sx={{ maxWidth: 960 }}>
      {onBack && (
        <Button startIcon={<ArrowBackIcon />} onClick={onBack} sx={{ mb: 1 }}>
          Financial Center
        </Button>
      )}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
        <PaymentsIcon color="primary" fontSize="large" />
        <Typography variant="h5" component="h1">
          Expenses
        </Typography>
      </Box>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
        Receipts and bills you added to your books.
      </Typography>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress aria-label="Loading expenses" />
        </Box>
      )}

      {loadError && !loading && (
        <Alert
          severity="warning"
          action={
            <Button color="inherit" size="small" onClick={load}>
              Try again
            </Button>
          }
        >
          {loadError}
        </Alert>
      )}

      {!loading && !loadError && rows.length === 0 && (
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Nothing here yet. Open a receipt in Receipts & bills and choose “Add to my books”.
          </Typography>
          {onOpenReceipts && (
            <Button variant="contained" onClick={onOpenReceipts}>
              Go to Receipts & bills
            </Button>
          )}
        </Box>
      )}

      {!loading && !loadError && rows.length > 0 && (
        <>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            sx={{ mb: 2 }}
            alignItems={{ sm: 'flex-start' }}
            useFlexGap
            flexWrap="wrap"
          >
            <TextField
              select
              label="Property"
              value={propertyId}
              onChange={(e) => setPropertyId(e.target.value)}
              slotProps={{ select: { displayEmpty: true }, inputLabel: { shrink: true } }}
              sx={{ minWidth: 220 }}
            >
              <MenuItem value={ALL}>All properties</MenuItem>
              {properties.map((p) => (
                <MenuItem key={p.id} value={p.id}>
                  {p.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Dates"
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
              sx={{ minWidth: 180 }}
            >
              <MenuItem value={ALL_DATES}>All dates</MenuItem>
              {years.map((y) => (
                <MenuItem key={y} value={y}>
                  {`${y} tax year`}
                </MenuItem>
              ))}
              <MenuItem value={CUSTOM}>Choose dates…</MenuItem>
            </TextField>
            {period === CUSTOM && (
              <>
                <TextField
                  type="date"
                  label="From"
                  value={customFrom}
                  onChange={(e) => setCustomFrom(e.target.value)}
                  slotProps={{ inputLabel: { shrink: true } }}
                />
                <TextField
                  type="date"
                  label="To"
                  value={customTo}
                  onChange={(e) => setCustomTo(e.target.value)}
                  error={Boolean(rangeError)}
                  helperText={rangeError}
                  slotProps={{ inputLabel: { shrink: true } }}
                />
              </>
            )}
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={download}
              disabled={downloading || shown.length === 0}
              sx={{ alignSelf: { sm: 'center' }, ml: { sm: 'auto' } }}
            >
              {downloading ? 'Preparing…' : 'Download CSV'}
            </Button>
          </Stack>
          {downloadError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setDownloadError(null)}>
              {downloadError}
            </Alert>
          )}
          <Stack direction="row" spacing={4} sx={{ mb: 2 }} useFlexGap flexWrap="wrap">
            {shown.length === 0 && !rangeError && (
              <Typography variant="body2" color="text.secondary">
                No expenses match these choices.
              </Typography>
            )}
            {totals.map((t) => (
              <Box key={t.currency} data-testid={`expense-total-${t.currency}`}>
                <Typography variant="h6" component="p">
                  {formatCents(t.amountCents, t.currency)} {totals.length > 1 ? t.currency : ''}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {shown.length === 1 ? '1 expense' : `${shown.length} expenses`} · tax{' '}
                  {formatCents(t.taxCents, t.currency)}
                </Typography>
              </Box>
            ))}
          </Stack>

          {lineTotals.length > 0 && (
            <Box component="section" aria-labelledby="t776-totals-heading" sx={{ mb: 3 }}>
              <Typography id="t776-totals-heading" variant="subtitle1" component="h2" sx={{ fontWeight: 600 }}>
                Totals by T776 line
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                {period === ALL_DATES && 'Pick a tax year above so these match one year’s T776. '}
                Totals include sales tax. Most residential landlords can’t claim GST/HST back, so these are the
                amounts for the form. If you claim input tax credits, use the amount before tax.
              </Typography>
              <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflowX: 'auto' }}>
                <Table
                  size="small"
                  aria-labelledby="t776-totals-heading"
                  sx={{ '& .MuiTableCell-root': { px: { xs: 1.25, sm: 2 } } }}
                >
                  <TableHead>
                    <TableRow>
                      <TableCell>Line</TableCell>
                      <TableCell>Category</TableCell>
                      <TableCell align="right">Total</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {lineTotals.map((t) => (
                      <TableRow key={t.key} data-testid={`t776-line-${t.craLine || 'none'}`}>
                        <TableCell sx={{ fontVariantNumeric: 'tabular-nums' }}>{t.craLine || '—'}</TableCell>
                        <TableCell>
                          {t.label}
                          <Typography variant="caption" color="text.secondary" display="block">
                            {t.count === 1 ? '1 expense' : `${t.count} expenses`}
                          </Typography>
                        </TableCell>
                        <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                          {formatCents(t.amountCents, t.currency)} {totals.length > 1 ? t.currency : ''}
                          <Typography variant="caption" color="text.secondary" display="block">
                            before tax {formatCents(t.amountCents - t.taxCents, t.currency)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block">
                            tax {formatCents(t.taxCents, t.currency)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </Box>
          )}

          {shown.length > 0 && (
            <List disablePadding sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
              {shown.map((row, index) => (
                <ListItemButton
                  key={row.id}
                  divider={index < shown.length - 1}
                  onClick={() => openReceipt(row)}
                  aria-label={`Open receipt: ${row.vendor || row.description || 'Expense'}`}
                >
                  <ListItemText
                    primary={row.vendor || row.description || 'Expense'}
                    secondary={[formatDay(row.transactionDate), placeLabel(row), row.categoryLabel]
                      .filter(Boolean)
                      .join(' · ')}
                  />
                  <Box sx={{ textAlign: 'right', pl: 2, flexShrink: 0 }}>
                    <Typography variant="body1">{formatCents(row.amountCents, row.currency)}</Typography>
                    {row.taxAmountCents != null && (
                      <Typography variant="caption" color="text.secondary">
                        tax {formatCents(row.taxAmountCents, row.currency)}
                      </Typography>
                    )}
                  </Box>
                </ListItemButton>
              ))}
            </List>
          )}
        </>
      )}

      <ExpenseDraftReview
        review={review}
        onClose={() => setReview(null)}
        onRetry={() => {}}
        onSaved={() => {
          setReview(null);
          load();
        }}
        onReopened={(draft, message) => {
          setReview((prev) => prev && { ...prev, doc: { ...prev.doc, draft }, notice: message });
          load();
        }}
      />
    </Box>
  );
}

export default ExpenseLedger;
